import { FC, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { StoreApi, createStore, useStore } from 'zustand';
import { defaultSettings } from '../../../services/settings/systemSettings.types';
import { useTimeSlots } from './ganttHelper';

export interface SubsectionConfig {
  id: string;
  defaultLength: number;
  minLength?: number;
  maxLength?: number;
}

interface GanttStore {
  containerId: string;
  minTime: number;
  maxTime: number;
  minShownTime: number;
  maxShownTime: number;
  increment: number;
  subsections?: SubsectionConfig[];
  defaultMainLength: number;
  minMainLength: number;
  currentlyDragging?: string;
  setCurrentlyDragging: (newVal?: string) => void;
  getSubsectionConfig: (id?: string) => SubsectionConfig | undefined;
  markerLines?: GanttMarkerLine[];
}

export interface GanttMarkerLine {
  time: number;
  color: string;
  label?: string;
}

interface Props extends PropsWithChildren {
  containerId: string;
  minTime: number;
  maxTime: number;
  minuteIncrement?: number;
  subsections?: SubsectionConfig[];
  defaultMainLength?: number;
  minMainLength?: number;
  markerLines?: GanttMarkerLine[];
}

const GanttContext = createContext<StoreApi<GanttStore> | null>(null);

export const DEFAULT_MINUTE_INCREMENT = defaultSettings.minuteIncrement;
export const DEFAULT_MAIN_LENGTH_IN_MINUTES = 120;

export const GanttProvider: FC<Props> = ({
  containerId,
  children,
  subsections,
  minTime,
  maxTime,
  minuteIncrement = DEFAULT_MINUTE_INCREMENT,
  defaultMainLength: defaultMainLengthInMinutes = DEFAULT_MAIN_LENGTH_IN_MINUTES * 60,
  minMainLength: minMainLengthInMinutes = 0,
  markerLines
}) => {
  const store = useMemo(() => {
    return createStore<GanttStore>(set => ({
      containerId,
      minTime,
      maxTime,
      minShownTime: Math.floor(minTime / (60 * 60)) * 60 * 60,
      maxShownTime: Math.ceil(maxTime / (60 * 60)) * 60 * 60,
      increment: minuteIncrement * 60,
      subsections,
      defaultMainLength: defaultMainLengthInMinutes,
      markerLines,
      minMainLength: minMainLengthInMinutes,
      setCurrentlyDragging: (newVal?: string) => {
        set(state => ({ ...state, currentlyDragging: newVal }));
      },
      getSubsectionConfig: (id?: string) => {
        return subsections?.find(sub => sub.id === id);
      }
    }));
  }, [minTime, maxTime, minuteIncrement, subsections, defaultMainLengthInMinutes, markerLines, minMainLengthInMinutes]);

  return <GanttContext.Provider value={store}>{children}</GanttContext.Provider>;
};

type SelectorReturn<S extends (s: GanttStore) => any> = ReturnType<S>;

export function useGanttService<S extends (s: GanttStore) => any>(selector: S): SelectorReturn<S> {
  const context = useContext(GanttContext);
  if (!context) {
    throw new Error('useGanttService must be used within a GanttProvider');
  }
  return useStore(context, selector);
}

export const useGanttTimeSlots = () => {
  const { minTime, maxTime, increment } = useGanttService(state => ({
    minTime: state.minShownTime,
    maxTime: state.maxShownTime,
    increment: state.increment
  }));

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(() => useTimeSlots(minTime, maxTime, increment), [minTime, maxTime, increment]);
};

export const useGanttSubsectionConfig = () => {
  return useGanttService(s => s.getSubsectionConfig);
};
